import { Hourglass } from "lucide-react";
import { simulateDebts, startBalance, paidPct, milestoneTimeline } from "../lib/finance";
import { cn } from "../lib/utils";
import { ShareButton } from "./ShareCard";
import { fmt, monthLabel } from "../lib/format";
import { Section } from "./common";

const shortDate = (s) => `${new Date(`${s}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })} '${s.slice(2, 4)}`;

export function MilestoneTimeline({ debt, items: given, id: givenId, testid, className }) {
  const items = given || milestoneTimeline(debt);
  const id = givenId || debt.id;
  return (
    <ol className={cn("flex items-start", className)} data-testid={testid || `debt-milestones-${id}`}>
      {items.map((m, i) => (
        <li key={m.pct} className="flex-1 min-w-[52px] relative" data-testid={`milestone-${id}-${m.pct}`}>
          {i > 0 && <span className={cn("absolute top-[7px] right-1/2 w-full h-0.5", m.reached ? "bg-[hsl(var(--income))]" : "bg-border")} />}
          <span className={cn("relative mx-auto block h-4 w-4 rounded-full border-2", m.reached ? "bg-[hsl(var(--income))] border-[hsl(var(--income))]" : "bg-card border-border")} />
          <p className={cn("text-[10px] text-center mt-1 font-semibold num", m.reached ? "text-income" : "text-muted-foreground")}>{m.pct}%</p>
          <p className="text-[10px] text-center text-muted-foreground leading-tight" data-testid={`milestone-date-${id}-${m.pct}`}>{m.date ? shortDate(m.date) : m.reached ? "earlier" : "—"}</p>
        </li>
      ))}
    </ol>
  );
}

export function ProgressRing({ pct, size = 88, stroke = 9, testid }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, pct));
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} title={`${Math.round(p)}% paid`} data-testid={testid}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--secondary))" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--income))" strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - p / 100)} style={{ transition: "stroke-dashoffset 900ms cubic-bezier(.3,.7,.3,1)" }} />
      </svg>
      {size >= 50 ? <span className="absolute inset-0 grid place-items-center font-display font-extrabold text-lg num">{Math.round(p)}%</span> : <span className="sr-only">{Math.round(p)}%</span>}
    </div>
  );
}

export { paidPct };

function DebtCard({ d, extraSim }) {
  const sim = extraSim || simulateDebts([d], 0, "minimum");
  const start = startBalance(d);
  return (
    <div className="rounded-2xl border bg-card p-4 flex gap-4 items-center" data-testid={`debt-countdown-${d.id}`}>
      <ProgressRing pct={paidPct(d)} testid={`debt-ring-${d.id}`} />
      <div className="min-w-0">
        <p className="font-semibold truncate">{d.creditor}</p>
        {sim.neverPaid ? (
          <p className="text-sm text-expense" data-testid={`debt-months-left-${d.id}`}>Payment too low — never paid off</p>
        ) : (
          <>
            <p className="font-display text-2xl font-extrabold leading-tight" data-testid={`debt-months-left-${d.id}`}>{sim.months} <span className="text-sm font-medium text-muted-foreground">month{sim.months === 1 ? "" : "s"} left</span></p>
            <p className="text-xs text-muted-foreground" data-testid={`debt-payoff-date-${d.id}`}>Debt-free by {monthLabel(sim.payoffMonth)}</p>
          </>
        )}
        <p className="text-xs text-muted-foreground mt-1">{fmt(start - Number(d.balance))} paid of {fmt(start)}</p>
        <MilestoneTimeline debt={d} className="mt-2" />
        <ShareButton debt={d} className="h-7 px-2 mt-1 -ml-2 text-xs" />
      </div>
    </div>
  );
}

export default function DebtCountdown({ debts, strategySim }) {
  const per = Object.fromEntries((strategySim?.perDebt || []).map((p) => [p.id, p]));
  return (
    <Section title="Payoff countdown" subtitle="Months left at your current payments (including the extra amount below, avalanche order). Ring = share of the balance paid since you added the debt." action={<Hourglass className="h-5 w-5 text-muted-foreground" />} testid="debt-countdown">
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3 stagger">
        {debts.map((d) => {
          const p = per[d.id];
          return <DebtCard key={d.id} d={d} extraSim={p && !strategySim.neverPaid ? { months: p.months, payoffMonth: p.payoffMonth, neverPaid: !p.payoffMonth } : null} />;
        })}
      </div>
    </Section>
  );
}
